import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Sparkle, ArrowRight, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const LOGO_URL = "/smart-logo.jpeg";

export default function AuthPage() {
    const [searchParams] = useSearchParams();
    // Default to "register" — the CEO wants new visitors to see the sign-up
    // form immediately. Existing users can flip to Sign In via the tab.
    const initialMode = searchParams.get("mode") || "register";
    const initialRole = searchParams.get("role") || "customer";
    const { user, login, register } = useAuth();
    const navigate = useNavigate();

    // Auto-redirect already-authenticated users so they don't see the login
    // form again when bouncing on the root URL. Respect `?next=…` so a pro
    // sent here from /become-a-pro lands back there after registering.
    const nextUrl = searchParams.get("next");
    useEffect(() => {
        if (!user) return;
        if (nextUrl && nextUrl.startsWith("/")) {
            navigate(nextUrl, { replace: true });
            return;
        }
        if (user.role === "admin") navigate("/admin", { replace: true });
        else if (user.role === "provider") navigate("/provider", { replace: true });
        else navigate("/home", { replace: true });
    }, [user, navigate, nextUrl]);

    const [loading, setLoading] = useState(false);
    const initialReferral = (searchParams.get("ref") || "").toUpperCase();
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [regForm, setRegForm] = useState({
        name: "",
        email: "",
        password: "",
        phone: "",
        role: initialRole,
        referral_code: initialReferral,
    });

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(loginForm.email, loginForm.password);
        setLoading(false);
        if (res.ok) {
            toast.success(`Welcome back, ${res.user.name?.split(" ")[0]}.`);
            redirectByRole(res.user.role);
        } else {
            toast.error(res.error);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await register(regForm);
        setLoading(false);
        if (res.ok) {
            toast.success("Account created. Welcome to SMART.");
            redirectByRole(res.user.role);
        } else {
            toast.error(res.error);
        }
    };

    const redirectByRole = (role) => {
        if (nextUrl && nextUrl.startsWith("/")) {
            navigate(nextUrl);
            return;
        }
        if (role === "admin") navigate("/admin");
        else if (role === "provider") navigate("/become-provider");
        else navigate("/services");
    };

    return (
        <div className="min-h-screen flex" data-testid="auth-page">
            {/* Left visual */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/8425392/pexels-photo-8425392.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
                    alt="SMART"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-emerald2/40" />
                <div className="relative p-16 flex flex-col justify-between w-full">
                    <Link to="/home" className="flex items-center gap-3" data-testid="auth-brand-link">
                        <img src={LOGO_URL} alt="SMART DEMANDS 24/7 LIMITED" className="h-14 w-14 object-contain" />
                        <div className="leading-tight">
                            <div className="font-serif text-2xl tracking-[0.15em]">SMART</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase text-amber-300/80 mt-0.5" data-testid="auth-llc-name-desktop">
                                DEMANDS 24/7 LIMITED
                            </div>
                        </div>
                    </Link>
                    <div>
                        <div className="overline mb-4">Premium Concierge</div>
                        <h1 className="font-serif text-5xl xl:text-6xl tracking-tighter leading-tight max-w-md">
                            Your demands,<br />
                            <span className="italic gold-grad">24/7.</span><br />
                            Anytime, Anywhere.
                        </h1>
                    </div>
                    <div>
                        <div className="overline opacity-60">Verified · Insured · 24/7</div>
                        <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-amber-300/70" data-testid="auth-llc-footer-desktop">
                            © SMART DEMANDS 24/7 LIMITED
                        </div>
                        <div className="text-[9px] tracking-[0.25em] uppercase text-muted-foreground/60 mt-0.5">
                            Duly incorporated under the Laws of Jamaica
                        </div>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 lg:px-16 py-12 bg-background">
                <div className="w-full max-w-md">
                    <Link to="/home" className="flex lg:hidden items-center gap-3 mb-10" data-testid="auth-brand-link-mobile">
                        <img src={LOGO_URL} alt="SMART DEMANDS 24/7 LIMITED" className="h-12 w-12 object-contain" />
                        <div className="leading-tight">
                            <div className="font-serif text-2xl tracking-[0.15em]">SMART</div>
                            <div className="text-[8px] tracking-[0.28em] uppercase text-amber-300/80 mt-0.5" data-testid="auth-llc-name-mobile">
                                DEMANDS 24/7 LIMITED
                            </div>
                        </div>
                    </Link>

                    <Tabs defaultValue={initialMode} className="w-full">
                        <div className="mb-6 flex items-center justify-between gap-3">
                            <Link
                                to="/join"
                                data-testid="auth-join-lanes-link"
                                className="text-[10px] uppercase tracking-[0.26em] text-[#FFD700] hover:underline inline-flex items-center gap-1.5"
                            >
                                ← Pick your lane first
                            </Link>
                            <Link
                                to="/home"
                                className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                                data-testid="auth-guest-browse"
                            >
                                Browse as guest <Home size={11} />
                            </Link>
                        </div>
                        <TabsList className="grid w-full grid-cols-2 bg-card border border-white/10 rounded-sm p-1 mb-10">
                            <TabsTrigger
                                value="login"
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                                data-testid="tab-login"
                            >
                                Sign In
                            </TabsTrigger>
                            <TabsTrigger
                                value="register"
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                                data-testid="tab-register"
                            >
                                Create Account
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="login">
                            <h2 className="font-serif text-4xl tracking-tight mb-2">Welcome back.</h2>
                            <p className="text-sm text-muted-foreground mb-8">
                                Sign in to manage your bookings.
                            </p>
                            <div className="mb-6" data-testid="login-google-block">
                                <GoogleSignInButton next={nextUrl} />
                                <div className="flex items-center gap-3 my-6">
                                    <div className="flex-1 h-px bg-white/10" />
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Or with email</span>
                                    <div className="flex-1 h-px bg-white/10" />
                                </div>
                            </div>
                            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                                <Field label="Email" htmlFor="login-email">
                                    <Input
                                        id="login-email"
                                        type="email"
                                        required
                                        value={loginForm.email}
                                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="login-email-input"
                                    />
                                </Field>
                                <Field label="Password" htmlFor="login-password">
                                    <Input
                                        id="login-password"
                                        type="password"
                                        required
                                        value={loginForm.password}
                                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="login-password-input"
                                    />
                                </Field>
                                <div className="flex justify-end -mt-2">
                                    <Link
                                        to="/forgot-password"
                                        className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:gold-text transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? "Signing in..." : "Sign In"} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                                </Button>
                            </form>
                        </TabsContent>

                        <TabsContent value="register">
                            <h2 className="font-serif text-4xl tracking-tight mb-2">Begin your concierge.</h2>
                            <p className="text-sm text-muted-foreground mb-8">
                                Three minutes to set up. A lifetime of effortless luxury.
                            </p>
                            <div className="mb-6" data-testid="register-google-block">
                                <GoogleSignInButton
                                    role={regForm.role}
                                    referralCode={regForm.referral_code}
                                    next={nextUrl}
                                    label="Sign up with Google"
                                />
                                <div className="flex items-center gap-3 my-6">
                                    <div className="flex-1 h-px bg-white/10" />
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Or with email</span>
                                    <div className="flex-1 h-px bg-white/10" />
                                </div>
                            </div>
                            <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
                                <Field label="Full Name" htmlFor="reg-name">
                                    <Input
                                        id="reg-name"
                                        required
                                        value={regForm.name}
                                        onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="register-name-input"
                                    />
                                </Field>
                                <Field label="Email" htmlFor="reg-email">
                                    <Input
                                        id="reg-email"
                                        type="email"
                                        required
                                        value={regForm.email}
                                        onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="register-email-input"
                                    />
                                </Field>
                                <Field label="Phone (optional)" htmlFor="reg-phone">
                                    <Input
                                        id="reg-phone"
                                        value={regForm.phone}
                                        onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="register-phone-input"
                                    />
                                </Field>
                                <Field label="Password (6+ chars)" htmlFor="reg-password">
                                    <Input
                                        id="reg-password"
                                        type="password"
                                        required
                                        minLength={6}
                                        value={regForm.password}
                                        onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="register-password-input"
                                    />
                                </Field>
                                <Field label="Referral code (optional · earn your friend SMART Coins)" htmlFor="reg-referral">
                                    <Input
                                        id="reg-referral"
                                        type="text"
                                        placeholder="SMART-XXXXX"
                                        value={regForm.referral_code}
                                        onChange={(e) => setRegForm({ ...regForm, referral_code: e.target.value.toUpperCase() })}
                                        className="bg-input border-white/10 h-11 rounded-sm font-mono tracking-widest"
                                        data-testid="register-referral-input"
                                        autoCapitalize="characters"
                                    />
                                </Field>
                                <Field label="I'm joining as" htmlFor="reg-role">
                                    <Select
                                        value={regForm.role}
                                        onValueChange={(v) => setRegForm({ ...regForm, role: v })}
                                    >
                                        <SelectTrigger
                                            id="reg-role"
                                            className="bg-input border-white/10 h-11 rounded-sm"
                                            data-testid="register-role-select"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-card border-white/10">
                                            <SelectItem value="customer" data-testid="register-role-customer">A customer (book services)</SelectItem>
                                            <SelectItem value="provider" data-testid="register-role-provider">A provider (offer services)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                    data-testid="register-submit-btn"
                                >
                                    {loading ? "Creating..." : "Create Account"} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>

                    <div className="mt-8 pt-6 border-t border-white/5 text-center">
                        <button
                            type="button"
                            onClick={() => navigate("/home")}
                            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-primary transition-colors"
                            data-testid="auth-browse-guest-btn"
                        >
                            <Home size={12} strokeWidth={1.5} />
                            Browse as Guest
                        </button>
                    </div>

                    <p className="mt-6 text-xs text-muted-foreground text-center">
                        By continuing you agree to our Terms.
                    </p>

                    <div
                        className="mt-10 pt-6 border-t border-white/5 text-center"
                        data-testid="auth-llc-footer"
                    >
                        <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/80">
                            © {new Date().getFullYear()} SMART DEMANDS 24/7 LIMITED
                        </div>
                        <div className="mt-1 text-[9px] tracking-[0.25em] uppercase text-muted-foreground/70">
                            A company duly incorporated under the Laws of Jamaica
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <div>
            <Label htmlFor={htmlFor} className="overline mb-2 block">
                {label}
            </Label>
            {children}
        </div>
    );
}
